'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Calendar, Field, FieldContent, FieldDescription, FieldError, FieldLabel, Popover, PopoverContent, PopoverTrigger, returnFocusUnlessMoved } from '@repo/ui/components';
import { DateFormatter } from '@repo/ui/date-formatter';
import { CalendarIcon } from '@repo/ui/icons';
import { mergeRefs } from '@repo/ui/lib/merge-refs';
import { cn } from '@repo/ui/lib/utils';
import { useStore } from '@tanstack/react-form';
import { ComponentProps, ReactNode, type Ref, useRef } from 'react';
import { ReadOnlyField, useReadOnly } from '../components/readonly.tsx';
import { useFieldContext } from '../lib/form-context.ts';
type FormDatePickerProps = {
  autoFocus?: boolean;
  calendarDisable?: ComponentProps<typeof Calendar>['disabled'];
  contentClassName?: string;
  description?: ReactNode | string;
  disabled?: boolean;
  isPending?: boolean;
  label?: ReactNode | string;
  required?: boolean;
  size?: 'default' | 'large';
  triggerClassName?: string;
  triggerRef?: Ref<HTMLButtonElement>;
};
type PopoverActions = {
  close: () => void;
  unmount: () => void;
};
export const FormDatePicker = t0 => {
  const $ = _c(54);
  const {
    autoFocus: t1,
    calendarDisable,
    contentClassName,
    description,
    disabled: t2,
    isPending: t3,
    label,
    required,
    size: t4,
    triggerClassName,
    triggerRef
  } = t0;
  const autoFocus = t1 === undefined ? false : t1;
  const disabled = t2 === undefined ? false : t2;
  const isPending = t3 === undefined ? false : t3;
  const size = t4 === undefined ? "default" : t4;
  const field = useFieldContext();
  let t5;
  if ($[0] !== field.state.meta.errors) {
    t5 = field.state.meta.errors ?? [];
    $[0] = field.state.meta.errors;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const errors = t5;
  const isInvalid = errors.length > 0;
  const popoverActionsRef = useRef(null);
  const popupRef = useRef(null);
  const triggerElementRef = useRef(null);
  const date = useStore(field.store, _temp);
  const isReadOnly = useReadOnly();
  let t6;
  if ($[2] !== field.name || $[3] !== label || $[4] !== required) {
    t6 = label && typeof label === "string" ? <FieldLabel className={cn(required && "required gap-0.5")} htmlFor={field.name}>{label}</FieldLabel> : label;
    $[2] = field.name;
    $[3] = label;
    $[4] = required;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const fieldLabel = t6;
  if (isReadOnly) {
    let t7;
    if ($[6] !== date) {
      t7 = date ? <DateFormatter date={date} kind="date" zone="utc" /> : "-";
      $[6] = date;
      $[7] = t7;
    } else {
      t7 = $[7];
    }
    let t8;
    if ($[8] !== fieldLabel || $[9] !== t7) {
      t8 = <ReadOnlyField label={fieldLabel}>{t7}</ReadOnlyField>;
      $[8] = fieldLabel;
      $[9] = t7;
      $[10] = t8;
    } else {
      t8 = $[10];
    }
    return t8;
  }
  let t7;
  if ($[11] !== triggerClassName) {
    t7 = cn("data-[empty=true]:text-muted-foreground w-fit justify-between text-left font-normal", triggerClassName);
    $[11] = triggerClassName;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = !date;
  const t9 = disabled || isPending;
  const t10 = field.name;
  let t11;
  if ($[13] !== triggerRef) {
    t11 = mergeRefs(triggerElementRef, triggerRef);
    $[13] = triggerRef;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const t12 = size === "large" ? "lg" : "default";
  let t13;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <CalendarIcon data-sentry-element="CalendarIcon" data-sentry-source-file="form-date-picker.tsx" />;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== date) {
    t14 = date ? <DateFormatter date={date} kind="date" zone="utc" /> : <span>Select a date</span>;
    $[16] = date;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== field.name || $[19] !== isInvalid || $[20] !== t11 || $[21] !== t12 || $[22] !== t14 || $[23] !== t7 || $[24] !== t8 || $[25] !== t9) {
    t15 = <PopoverTrigger asChild={true} data-sentry-element="PopoverTrigger" data-sentry-source-file="form-date-picker.tsx"><Button aria-invalid={isInvalid} className={t7} data-empty={t8} disabled={t9} id={t10} ref={t11} size={t12} type="button" variant="outline" data-sentry-element="Button" data-sentry-source-file="form-date-picker.tsx">{t13}{t14}</Button></PopoverTrigger>;
    $[18] = field.name;
    $[19] = isInvalid;
    $[20] = t11;
    $[21] = t12;
    $[22] = t14;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => returnFocusUnlessMoved(triggerElementRef, popupRef)();
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const t17 = autoFocus ? false : undefined;
  let t18;
  if ($[28] !== field) {
    t18 = next => {
      field.handleChange(next);
      if (next) {
        popoverActionsRef.current?.close();
      }
    };
    $[28] = field;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== autoFocus || $[31] !== calendarDisable || $[32] !== date || $[33] !== t18) {
    t19 = <Calendar autoFocus={autoFocus} defaultMonth={date} disabled={calendarDisable} mode="single" onSelect={t18} selected={date} data-sentry-element="Calendar" data-sentry-source-file="form-date-picker.tsx" />;
    $[30] = autoFocus;
    $[31] = calendarDisable;
    $[32] = date;
    $[33] = t18;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== t17 || $[36] !== t19) {
    t20 = <PopoverContent align="start" className="w-auto p-0" finalFocus={t16} initialFocus={t17} ref={popupRef} data-sentry-element="PopoverContent" data-sentry-source-file="form-date-picker.tsx">{t19}</PopoverContent>;
    $[35] = t17;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t15 || $[39] !== t20) {
    t21 = <Popover actionsRef={popoverActionsRef} data-sentry-element="Popover" data-sentry-source-file="form-date-picker.tsx">{t15}{t20}</Popover>;
    $[38] = t15;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== description) {
    t22 = description && typeof description === "string" ? <FieldDescription>{description}</FieldDescription> : description;
    $[41] = description;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== errors) {
    t23 = <FieldError className="text-sm" errors={errors} data-sentry-element="FieldError" data-sentry-source-file="form-date-picker.tsx" />;
    $[43] = errors;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== contentClassName || $[46] !== fieldLabel || $[47] !== t21 || $[48] !== t22 || $[49] !== t23) {
    t24 = <FieldContent className={contentClassName} data-sentry-element="FieldContent" data-sentry-source-file="form-date-picker.tsx">{fieldLabel}{t21}{t22}{t23}</FieldContent>;
    $[45] = contentClassName;
    $[46] = fieldLabel;
    $[47] = t21;
    $[48] = t22;
    $[49] = t23;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== isInvalid || $[52] !== t24) {
    t25 = <Field className="gap-1" data-invalid={isInvalid} data-sentry-element="Field" data-sentry-component="FormDatePicker" data-sentry-source-file="form-date-picker.tsx">{t24}</Field>;
    $[51] = isInvalid;
    $[52] = t24;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  return t25;
};
function _temp(store) {
  return store.value;
}